/**
 * Google tag bootstrap — Google Tag Manager or gtag.js, configured from env.
 *
 * Owns: loading the Google script once, and the `trackEvent` / `trackPageView`
 * seam game code calls. Nothing else in the app talks to `dataLayer`.
 *
 * The id comes from the runtime env (`window.__ENV__.GTAG_ID`, written by the
 * container entrypoint) with the build-time `VITE_GTAG_ID` as fallback — the
 * same resolution order as API_URL. No id → every call is a silent no-op, so
 * games that never set it carry no tracking at all.
 *
 * One variable covers both products; the prefix decides the loader:
 *   GTM-XXXXXXX           → Tag Manager container (tags configured in GTM)
 *   G- / AW- / DC-XXXXXXX → gtag.js (GA4 / Ads / Floodlight, configured here)
 *
 * SPA page views are sent by `trackPageView` from the router — the initial
 * automatic page_view is turned off so a redirect does not count twice.
 */

type DataLayerEntry = Record<string, unknown> | IArguments;

declare global {
  interface Window {
    dataLayer?: DataLayerEntry[];
    gtag?: (...args: unknown[]) => void;
  }
}

export type EventParams = Record<string, string | number | boolean | undefined>;

// Google's own id formats; the id is interpolated into a script URL, so
// anything outside this shape is refused rather than loaded.
const GTM_ID_RE = /^GTM-[A-Z0-9]+$/;
const GTAG_ID_RE = /^(G|AW|DC)-[A-Z0-9]+$/;

const GTM_SRC = 'https://www.googletagmanager.com/gtm.js?id=';
const GTAG_SRC = 'https://www.googletagmanager.com/gtag/js?id=';

type Mode = 'gtm' | 'gtag';

let active: { id: string; mode: Mode } | null = null;

/** Returns the configured tag id, or '' when tracking is off. */
export function resolveTagId(): string {
  const raw = window.__ENV__?.GTAG_ID || import.meta.env.VITE_GTAG_ID || '';
  return raw.trim();
}

function modeFor(id: string): Mode | null {
  if (GTM_ID_RE.test(id)) return 'gtm';
  if (GTAG_ID_RE.test(id)) return 'gtag';
  return null;
}

function appendScript(src: string) {
  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

/**
 * Loads the Google script for the configured id. Idempotent: a second call is
 * ignored. Returns true when tracking is now active.
 */
export function initAnalytics(): boolean {
  if (active) return true;

  const id = resolveTagId();
  if (!id) return false;

  const mode = modeFor(id);
  if (!mode) {
    console.warn(`[analytics] ignoring GTAG_ID "${id}": not a GTM-/G-/AW-/DC- id`);
    return false;
  }

  window.dataLayer = window.dataLayer ?? [];

  if (mode === 'gtm') {
    // Verbatim from Google's container snippet, minus the inline <script>.
    window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
    appendScript(`${GTM_SRC}${encodeURIComponent(id)}`);
  } else {
    // gtag() must queue `arguments` objects — gtag.js replays them on load.
    window.gtag = function gtag() {
      window.dataLayer!.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', id, { send_page_view: false });
    appendScript(`${GTAG_SRC}${encodeURIComponent(id)}`);
  }

  active = { id, mode };
  return true;
}

/** True once `initAnalytics` loaded a tag. */
export function isAnalyticsActive(): boolean {
  return active !== null;
}

/**
 * Sends a named event. Under GTM it is a dataLayer push (`{ event, ...params }`,
 * matched by a Custom Event trigger); under gtag.js it is `gtag('event', …)`.
 */
export function trackEvent(name: string, params: EventParams = {}): void {
  if (!active) return;
  if (active.mode === 'gtm') {
    window.dataLayer!.push({ event: name, ...params });
  } else {
    window.gtag!('event', name, params);
  }
}

/** Page view for SPA navigation. Called by the router after each render. */
export function trackPageView(path: string = location.pathname + location.search): void {
  trackEvent('page_view', { page_path: path, page_location: location.href });
}

/** Test seam: forgets the loaded tag so the next init starts clean. */
export function resetAnalyticsForTests(): void {
  active = null;
}
